import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigation, useNotification } from "@refinedev/core";
import { RotateCcw } from "lucide-react";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { MultiSelect } from "@/components/fields/multi-select";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import { useGroupsRepository } from "@/pages/tina/groups/hooks/use-groups-repository";
import { formatWeekdays } from "@/pages/tina/groups/lib/weekdays";
import {
  CONTENT_TYPE_LABEL,
  countryFlag,
} from "@/pages/tina/runs/components/site-picker/utils";
import { useSiteFacets } from "@/pages/tina/sites/hooks/use-site-facets";
import { useSitesRepository } from "@/pages/tina/sites/hooks/use-sites-repository";
import { normalizeInstagramHandle } from "@/pages/tina/sites/lib/instagram-handle";
import {
  type SiteCreateFormType,
  siteCreateSchema,
} from "@/pages/tina/sites/schemas/site.create.form";
import type { InstagramSiteCreatePayload, TinaSiteGroup } from "@/types/tina";

interface FixedOption {
  value: string;
  label: string;
  /** Why this option cannot be picked yet; rendered muted next to the label. */
  unsupported?: string;
}

const SOURCE_OPTIONS: FixedOption[] = [
  { value: "instagram", label: "Instagram" },
  {
    value: "web",
    label: "Website / sitemap / API",
    unsupported: "not supported yet",
  },
  {
    value: "facebook_group",
    label: "Facebook group",
    unsupported: "not supported yet",
  },
];

const CONTENT_OPTIONS: FixedOption[] = [
  { value: "music_event", label: CONTENT_TYPE_LABEL.music_event },
  {
    value: "cinema_screening",
    label: CONTENT_TYPE_LABEL.cinema_screening,
    unsupported: "not supported for Instagram yet",
  },
];

/** A fixed-value radio list: the supported option plus greyed-out roadmap ones. */
function UnsupportedOptionRadios({
  label,
  name,
  value,
  options,
}: {
  label: string;
  name: string;
  value: string;
  options: FixedOption[];
}) {
  return (
    // `aria-labelledby` names the group; the reason a disabled option cannot be
    // picked is wired up with `aria-describedby` so it is not visual-only.
    <RadioGroup
      value={value}
      className="space-y-2"
      aria-labelledby={`${name}-label`}
    >
      <div id={`${name}-label`} className="text-sm font-medium">
        {label}
      </div>
      {options.map((option) => (
        <div key={option.value} className="flex items-center gap-2">
          <RadioGroupItem
            id={`${name}-${option.value}`}
            value={option.value}
            disabled={Boolean(option.unsupported)}
            aria-describedby={
              option.unsupported ? `${name}-${option.value}-reason` : undefined
            }
          />
          <Label htmlFor={`${name}-${option.value}`} className="font-normal">
            {option.label}
          </Label>
          {option.unsupported && (
            <span
              id={`${name}-${option.value}-reason`}
              className="text-xs text-muted-foreground"
            >
              {option.unsupported}
            </span>
          )}
        </div>
      ))}
    </RadioGroup>
  );
}

/**
 * Sentinel value for the city Select's "add a new city" row. Cities are free
 * text in the `sites` table, so a first-in-city source has to be able to name
 * one — but typing must not be the *default* path, or `athens` accumulates
 * beside `Athens`.
 */
const NEW_CITY = "__new_city__";

/**
 * Reuse a known city's spelling when the curator types one that already exists
 * in another case. A second `athens` bucket next to `Athens` is exactly the
 * drift the facet list exists to prevent.
 */
function canonicalCity(typed: string, known: string[]): string {
  const trimmed = typed.trim();
  if (!trimmed) return "";
  return (
    known.find((city) => city.toLowerCase() === trimmed.toLowerCase()) ??
    trimmed
  );
}

/**
 * Does a 422 from `POST /sites` say the curator's group list is stale?
 *
 * Tina raises one such 422 per point it can notice a vanished group — today
 * `unknown site groups: [...]` (the id was already gone when the body was
 * validated) and `site groups changed during create: [...]` (the group was
 * deleted between that check and the membership insert) — and every one of them
 * means the same thing here: reload the list and say so. Matching the English
 * phrase rather than either exact message keeps a future third wording covered.
 *
 * The space is load-bearing. Pydantic's own validation-error array names the
 * *field*, `site_groups: Extra inputs are not permitted` (what a backend
 * predating the field returns), which is not a stale-group situation and has to
 * stay on the generic toast where the curator can read it verbatim.
 */
const STALE_SITE_GROUPS_DETAIL = /\bsite groups?\b/i;

function groupLabel(group: TinaSiteGroup): string {
  const time = group.time_of_day.slice(0, 5); // "HH:MM:SS" -> "HH:MM"
  const schedule = `${time}, ${formatWeekdays(group.days_of_week)}`;
  return `${group.name} — ${schedule}${group.enabled ? "" : " (paused)"}`;
}

export function SiteForm() {
  const repo = useSitesRepository();
  const groupsRepo = useGroupsRepository();
  const { open } = useNotification();
  const { list } = useNavigation();

  const form = useForm<SiteCreateFormType>({
    resolver: zodResolver(siteCreateSchema),
    defaultValues: {
      handle: "",
      name: "",
      country_code: "",
      city: "",
      site_groups: [],
      enabled: true,
    },
  });

  const [nameTouched, setNameTouched] = useState(false);
  const [groups, setGroups] = useState<TinaSiteGroup[]>([]);
  const [handleWarning, setHandleWarning] = useState<string | null>(null);
  const handleRef = useRef<HTMLInputElement | null>(null);

  const [cityFreeText, setCityFreeText] = useState(false);

  const countryCode = form.watch("country_code");
  const normalized = normalizeInstagramHandle(form.watch("handle"));
  // Both lists come from /sites/facets: a curator picks a country Mood already
  // covers rather than minting one, and the city list is the anti-drift
  // reference. Deliberately NOT scoped by content type or source kind — the
  // first Instagram source in a country/city that so far has only web sources
  // must still find the existing country code and city spelling. `countries` is
  // unaffected by countryCode; `cities` is scoped to it (see useSiteFacets).
  const { countries, cities } = useSiteFacets({ countryCode });

  async function reloadGroups() {
    try {
      setGroups(await groupsRepo.list());
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  useEffect(() => {
    void reloadGroups();
    // The repository object is re-created on every render; load groups once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The name defaults to the handle until the curator overrides it by hand.
  useEffect(() => {
    if (nameTouched) return;
    form.setValue("name", normalized);
    // A 409 sets a manual error naming one exact string; re-mirroring makes that
    // message describe a name the field no longer holds, so drop it.
    form.clearErrors("name");
  }, [normalized, nameTouched, form]);

  /** Advisory duplicate-handle check — it warns, it never blocks the create. */
  async function checkExistingHandle() {
    if (!normalized) {
      setHandleWarning(null);
      return;
    }
    const rows = await repo.findByHandle(normalized);
    const match = rows.find(
      (row) =>
        row.config?.instagram_handle === normalized ||
        row.name === normalized,
    );
    setHandleWarning(
      match
        ? `${match.name} already scrapes @${normalized} — creating this site would double-scrape the profile.`
        : null,
    );
  }

  const onSubmit = async (values: SiteCreateFormType) => {
    // The schema validates the handle but does not rewrite it, so a pasted
    // profile URL still has to be normalized before it reaches the config.
    const payload: InstagramSiteCreatePayload = {
      name: values.name,
      content_type: "music_event",
      x_strategy: "instagram",
      country_code: values.country_code,
      city: values.city,
      config: { instagram_handle: normalizeInstagramHandle(values.handle) },
      enabled: values.enabled,
      source: "manual",
      // Omitted rather than sent empty: tina's SiteCreate is extra="forbid", so a
      // deployment predating `site_groups` 422s on the key even with []. Naming a
      // group does require the newer backend — that is the documented deploy order.
      ...(values.site_groups.length ? { site_groups: values.site_groups } : {}),
    };

    try {
      await repo.create(payload);
      const picked = groups
        .filter((group) => values.site_groups.includes(group.id))
        .map((group) => group.name);
      open?.({
        type: "success",
        message: `Created ${values.name}`,
        description: picked.length
          ? picked.join(", ")
          : "No group — will not run until scheduled",
      });
      // Location and groups stay put: adding a batch of sites for one city is
      // the common case.
      form.reset({ ...form.getValues(), handle: "", name: "" });
      setNameTouched(false);
      setHandleWarning(null);
      handleRef.current?.focus();
    } catch (error) {
      const status = (error as { statusCode?: number } | undefined)?.statusCode;
      if (status === 409) {
        form.setError("name", {
          type: "manual",
          message: `A site named "${values.name}" already exists`,
        });
        return;
      }
      const detail = toHumanErrorMessage(error);
      if (status === 422 && STALE_SITE_GROUPS_DETAIL.test(detail)) {
        open?.({
          type: "error",
          message: "Group no longer exists",
          description:
            "One of the selected groups was deleted. The list has been refreshed.",
        });
        void reloadGroups();
        return;
      }
      open?.({
        type: "error",
        message: "Failed to create site",
        description: detail,
      });
      Sentry.captureException(error);
    }
  };

  return (
    <Form {...form}>
      {/* pb-24 keeps the last field clear of the fixed BottomActionBar. */}
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex max-w-xl flex-col gap-6 pb-24"
      >
        <UnsupportedOptionRadios
          label="Source type"
          name="source-type"
          value="instagram"
          options={SOURCE_OPTIONS}
        />

        <UnsupportedOptionRadios
          label="Content type"
          name="content-type"
          value="music_event"
          options={CONTENT_OPTIONS}
        />

        <FormField
          control={form.control}
          name="handle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Instagram handle or URL</FormLabel>
              <FormControl>
                <Input
                  placeholder="@alaska.ath"
                  {...field}
                  ref={(element) => {
                    field.ref(element);
                    handleRef.current = element;
                  }}
                  onChange={(event) => {
                    // The warning names the handle it was computed for; it is only
                    // recomputed on blur, so it must not outlive the typed value.
                    setHandleWarning(null);
                    field.onChange(event);
                  }}
                  onBlur={() => {
                    field.onBlur();
                    void checkExistingHandle();
                  }}
                />
              </FormControl>
              {normalized && (
                <FormDescription>{`→ handle: ${normalized}`}</FormDescription>
              )}
              <FormMessage />
              {handleWarning && (
                <p className="text-sm text-amber-600">{handleWarning}</p>
              )}
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Site name</FormLabel>
              <div className="flex items-center gap-2">
                <FormControl>
                  <Input
                    {...field}
                    onChange={(event) => {
                      setNameTouched(true);
                      // Same reason as in the mirroring effect: a 409 message names
                      // the previous value, so editing the field invalidates it.
                      form.clearErrors("name");
                      field.onChange(event);
                    }}
                  />
                </FormControl>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="reset to handle"
                  onClick={() => {
                    setNameTouched(false);
                    form.setValue("name", normalized);
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
              <FormDescription>
                Identifier — cannot be changed later.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="country_code"
          render={({ field }) => (
            <FormItem>
              {/* These three pickers are not FormControl children (the Slot's id
                  would land on their outer div, not the trigger), so they are
                  named explicitly instead of via FormLabel's generated id. */}
              <FormLabel id="site-country-label" htmlFor="site-country">
                Country
              </FormLabel>
              <Select value={field.value || undefined} onValueChange={field.onChange}>
                <SelectTrigger
                  id="site-country"
                  aria-labelledby="site-country-label"
                  className="h-9"
                >
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                {/* Both location pickers sit mid-form, so Radix flips them
                    upward when the page is scrolled. Cap the list and keep it
                    off the viewport edge, or a long facet list opens past the
                    top of the screen and clips. */}
                <SelectContent
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  collisionPadding={8}
                  className="max-h-[min(18rem,var(--radix-select-content-available-height))]"
                >
                  {countries.map((code) => {
                    const flag = countryFlag(code);
                    return (
                      <SelectItem key={code} value={code}>
                        {flag ? <span className="mr-1.5">{flag}</span> : null}
                        {code}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <FormDescription>
                Countries Mood already covers. Adding a new one is not a
                curator action.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel id="site-city-label" htmlFor="site-city">
                City
              </FormLabel>
              {cityFreeText ? (
                <div className="flex items-center gap-2">
                  <Input
                    id="site-city"
                    aria-labelledby="site-city-label"
                    placeholder="New city name"
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                    // Snap to a known spelling on the way out, so typing
                    // "athens" under an existing "Athens" reuses the facet.
                    onBlur={() =>
                      field.onChange(canonicalCity(field.value, cities))
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCityFreeText(false);
                      field.onChange("");
                    }}
                  >
                    Pick from list
                  </Button>
                </div>
              ) : (
                <Select
                  value={field.value || undefined}
                  onValueChange={(value) => {
                    if (value === NEW_CITY) {
                      setCityFreeText(true);
                      field.onChange("");
                      return;
                    }
                    field.onChange(value);
                  }}
                  disabled={!countryCode}
                >
                  <SelectTrigger
                    id="site-city"
                    aria-labelledby="site-city-label"
                    className="h-9"
                  >
                    <SelectValue
                      placeholder={
                        countryCode ? "Select city" : "Pick a country first"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    collisionPadding={8}
                    className="max-h-[min(18rem,var(--radix-select-content-available-height))]"
                  >
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                    <SelectItem value={NEW_CITY}>＋ Add a new city…</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="site_groups"
          render={({ field }) => (
            <FormItem>
              <FormLabel id="site-groups-label" htmlFor="site-groups">
                Groups
              </FormLabel>
              <MultiSelect
                options={groups.map((group) => ({
                  label: groupLabel(group),
                  value: group.id,
                }))}
                selected={field.value}
                onChange={field.onChange}
                placeholder="No group"
                triggerId="site-groups"
                ariaLabelledBy="site-groups-label"
              />
              <FormDescription>
                Without a group, cron never picks this site up.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="enabled"
          render={({ field }) => (
            <FormItem className="flex items-center gap-3">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="!mt-0">Enabled</FormLabel>
            </FormItem>
          )}
        />

        <BottomActionBar className="justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => list("tina-sites")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            Create site
          </Button>
        </BottomActionBar>
      </form>
    </Form>
  );
}
